import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import calculateFloatingPosition from "../../../utils/floatingPosition";
import AnchoredPortalContext from "./AnchoredPortalContext";
import "./index.scss";

const DEFAULT_GAP = 12;
const DEFAULT_OFFSET = { x: 0, y: 10 };
const DEFAULT_Z_INDEX = 960;

const resolvePortalRoot = () => {
    if (typeof document === "undefined") {
        return null;
    }

    return document.getElementById("portal") || document.body;
};

export default function AnchoredPortal({
    id = undefined,
    anchorRef,
    children,
    onClose = undefined,
    className = "",
    placement = "bottom-start",
    offset = DEFAULT_OFFSET,
    gap = DEFAULT_GAP,
    flip = true,
    zIndex = DEFAULT_Z_INDEX,
}) {
    const panelRef = useRef(null);
    const frameRef = useRef(0);
    const ownedNodeRefs = useRef(new Set());
    const portalRoot = useMemo(() => resolvePortalRoot(), []);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const offsetX = offset?.x ?? DEFAULT_OFFSET.x;
    const offsetY = offset?.y ?? DEFAULT_OFFSET.y;

    const registerOwnedNode = useCallback((node) => {
        if (!node) {
            return () => {};
        }

        ownedNodeRefs.current.add(node);

        return () => {
            ownedNodeRefs.current.delete(node);
        };
    }, []);

    const contextValue = useMemo(
        () => ({ registerOwnedNode }),
        [registerOwnedNode],
    );

    const updatePosition = useCallback(() => {
        if (typeof document === "undefined") {
            return;
        }

        const anchorNode = anchorRef?.current;
        const panelNode = panelRef.current;

        if (!anchorNode || !panelNode) {
            return;
        }

        const anchorRect = anchorNode.getBoundingClientRect();
        const nextCoords = calculateFloatingPosition({
            container: document.documentElement,
            dropdown: panelNode,
            anchor: {
                top: anchorRect.top,
                left: anchorRect.left,
                width: anchorRect.width,
                height: anchorRect.height,
            },
            gap,
            offset: { x: offsetX, y: offsetY },
            placement,
            flip,
            strategy: "fixed",
        });

        setCoords((currentCoords) => {
            if (currentCoords.top === nextCoords.top && currentCoords.left === nextCoords.left) {
                return currentCoords;
            }

            return nextCoords;
        });
    }, [anchorRef, flip, gap, offsetX, offsetY, placement]);

    const schedulePositionUpdate = useCallback(() => {
        if (typeof window === "undefined") {
            return;
        }

        if (frameRef.current) {
            cancelAnimationFrame(frameRef.current);
        }

        frameRef.current = requestAnimationFrame(() => {
            frameRef.current = 0;
            updatePosition();
        });
    }, [updatePosition]);

    useLayoutEffect(() => {
        if (!portalRoot) {
            return undefined;
        }

        schedulePositionUpdate();

        return () => {
            if (frameRef.current) {
                cancelAnimationFrame(frameRef.current);
                frameRef.current = 0;
            }
        };
    }, [portalRoot, schedulePositionUpdate]);

    useEffect(() => {
        if (!portalRoot) {
            return undefined;
        }

        const handleViewportChange = () => {
            schedulePositionUpdate();
        };

        document.addEventListener("scroll", handleViewportChange, true);
        window.addEventListener("resize", handleViewportChange);

        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", handleViewportChange);
            window.visualViewport.addEventListener("scroll", handleViewportChange);
        }

        return () => {
            document.removeEventListener("scroll", handleViewportChange, true);
            window.removeEventListener("resize", handleViewportChange);

            if (window.visualViewport) {
                window.visualViewport.removeEventListener("resize", handleViewportChange);
                window.visualViewport.removeEventListener("scroll", handleViewportChange);
            }
        };
    }, [portalRoot, schedulePositionUpdate]);

    useEffect(() => {
        if (!portalRoot || typeof ResizeObserver === "undefined") {
            return undefined;
        }

        const observer = new ResizeObserver(() => {
            schedulePositionUpdate();
        });

        if (panelRef.current) {
            observer.observe(panelRef.current);
        }

        if (anchorRef?.current) {
            observer.observe(anchorRef.current);
        }

        return () => {
            observer.disconnect();
        };
    }, [anchorRef, portalRoot, schedulePositionUpdate]);

    useEffect(() => {
        if (!portalRoot || !onClose) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [onClose, portalRoot]);

    useEffect(() => {
        if (!portalRoot || !onClose) {
            return undefined;
        }

        const ownedNodes = ownedNodeRefs.current;
        let detachListeners = null;
        const listenerTimeoutId = window.setTimeout(() => {
            const handlePointerDown = (event) => {
                const anchorNode = anchorRef?.current;
                const panelNode = panelRef.current;
                const targetNode = event.target;
                const clickedOwnedPortal = Array.from(ownedNodes).some(
                    (node) => node?.contains?.(targetNode),
                );

                if (anchorNode?.contains(targetNode) || panelNode?.contains(targetNode) || clickedOwnedPortal) {
                    return;
                }

                onClose();
            };

            document.addEventListener("pointerdown", handlePointerDown, true);
            detachListeners = () => {
                document.removeEventListener("pointerdown", handlePointerDown, true);
            };
        }, 0);

        return () => {
            window.clearTimeout(listenerTimeoutId);
            detachListeners?.();
            ownedNodes.clear();
        };
    }, [anchorRef, onClose, portalRoot]);

    if (!portalRoot) {
        return null;
    }

    const portalClassName = [
        "anchored-portal",
        className,
    ].filter(Boolean).join(" ");

    return createPortal(
        <div
            id={id}
            ref={panelRef}
            className={portalClassName}
            style={{
                top: coords.top,
                left: coords.left,
                "--anchored-portal-z-index": String(zIndex),
            }}
        >
            <AnchoredPortalContext.Provider value={contextValue}>
                {children}
            </AnchoredPortalContext.Provider>
        </div>,
        portalRoot
    );
}

AnchoredPortal.propTypes = {
    id: PropTypes.string,
    anchorRef: PropTypes.shape({
        current: PropTypes.any,
    }).isRequired,
    children: PropTypes.node.isRequired,
    onClose: PropTypes.func,
    className: PropTypes.string,
    placement: PropTypes.oneOf([
        "top-start",
        "top-center",
        "top-end",
        "right-start",
        "right-center",
        "right-end",
        "bottom-start",
        "bottom-center",
        "bottom-end",
        "left-start",
        "left-center",
        "left-end",
    ]),
    offset: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number,
    }),
    gap: PropTypes.number,
    flip: PropTypes.bool,
    zIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
